import React, { useState, useEffect } from 'react';
import { templateAPI } from '../../services/templateApi';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/EditorTemplate.css';

/**
 * SelettoreTemplate
 * Associa a una prestazione uno o piu' modelli di consenso gia' esistenti.
 *
 * Props:
 *  - prestazioneId
 *  - giaAssociati   array di id gia' collegati, per escluderli
 *  - onClose()
 *  - onSuccess()
 */

const SelettoreTemplate = ({ prestazioneId, giaAssociati = [], onClose, onSuccess }) => {
    const [modelli, setModelli] = useState([]);
    const [selezionati, setSelezionati] = useState([]);
    const [ricerca, setRicerca] = useState('');
    const [caricamento, setCaricamento] = useState(true);
    const [salvataggio, setSalvataggio] = useState(false);
    const [errore, setErrore] = useState(null);

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            try {
                const r = await templateAPI.getAll();
                if (!attivo) return;
                const elenco = r.data.data.template || [];
                setModelli(elenco.filter((t) => !giaAssociati.includes(t.id)));
            } catch (e) {
                if (attivo) setErrore(e?.message || 'Modelli non caricati');
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        carica();
        return () => { attivo = false; };
        // eslint-disable-next-line
    }, [prestazioneId]);

    const commuta = (id) => {
        setSelezionati((prec) =>
            prec.includes(id) ? prec.filter((x) => x !== id) : [...prec, id]
        );
    };

    const conferma = async () => {
        if (selezionati.length === 0) return;
        try {
            setSalvataggio(true);
            setErrore(null);
            for (const id of selezionati) {
                await templateAPI.associa(id, prestazioneId);
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (e) {
            console.error('Errore associazione modelli:', e);
            setErrore(e?.message || "Errore nell'associazione");
        } finally {
            setSalvataggio(false);
        }
    };

    const filtrati = modelli.filter((t) => {
        if (!ricerca.trim()) return true;
        const testo = `${t.titolo} ${t.categoria || ''} ${t.medico_cognome || ''}`.toLowerCase();
        return testo.includes(ricerca.toLowerCase());
    });

    return (
        <div className="modulo-overlay">
            <div className="modulo-finestra sel-finestra" role="dialog" aria-modal="true">
                <button className="modulo-chiudi ed-chiudi" onClick={onClose} aria-label="Chiudi">✕</button>

                <div className="modulo-body">
                    <h2 className="modulo-titolo">Associa un consenso</h2>
                    <p className="modulo-nota">
                        I modelli selezionati verranno proposti automaticamente quando questa prestazione
                        viene eseguita.
                    </p>

                    <section className="modulo-sezione">
                        <input
                            className="input-modulo"
                            placeholder="Cerca per titolo, categoria o medico…"
                            value={ricerca}
                            onChange={(e) => setRicerca(e.target.value)}
                        />
                    </section>

                    <section className="modulo-sezione">
                        {caricamento ? (
                            <div className="ed-stato">Caricamento modelli…</div>
                        ) : filtrati.length === 0 ? (
                            <div className="ed-stato">
                                {modelli.length === 0
                                    ? 'Nessun modello disponibile da associare.'
                                    : 'Nessun modello corrisponde alla ricerca.'}
                            </div>
                        ) : (
                            <div className="sel-elenco">
                                {filtrati.map((t) => (
                                    <label key={t.id}
                                        className={`sel-voce ${selezionati.includes(t.id) ? 'scelta' : ''}`}>
                                        <input type="checkbox"
                                            checked={selezionati.includes(t.id)}
                                            onChange={() => commuta(t.id)} />
                                        <div className="sel-testo">
                                            <div className="sel-titolo">{t.titolo}</div>
                                            <div className="sel-meta">
                                                {t.categoria || 'Senza categoria'}
                                                {' · '}
                                                {t.medico_cognome
                                                    ? `Dott./Dott.ssa ${t.medico_cognome}`
                                                    : 'Modello di struttura'}
                                                {' · v'}{t.versione}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </section>

                    {errore && <div className="ed-errore">{errore}</div>}
                </div>

                <footer className="modulo-footer">
                    <button className="btn-annulla" onClick={onClose} disabled={salvataggio}>Annulla</button>
                    <button className="btn-conferma" onClick={conferma}
                        disabled={salvataggio || selezionati.length === 0}>
                        {salvataggio
                            ? 'Associazione…'
                            : `Associa${selezionati.length ? ` (${selezionati.length})` : ''}`}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default SelettoreTemplate;
